import { Spinner } from "@chakra-ui/react";
import { navigate } from "@reach/router";
import { Input } from "components/RFQ/Input";
import { CustomModal } from "components/RFQ/Modal/CustomModal";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { RFX_STATUS } from "utils/constants";
import { isNull } from "utils/validation";

import { Button } from "newui/components/Button";
import InfoBox from "newui/components/InfoBox";
import { setSwitchFromGuest } from "newui/features/Auth/utils";

import { acceptDeclineSubmission } from "../../submission-api";
import type { GuestCompanyData } from "../../types/guest-company-data.type";
import type { RfxSubmissionStatus } from "../../types/submission.type";
import { CompanyInfo } from "./CompanyInfo";

interface AcceptDeclineModalProps {
	showDialog: "accept" | "decline" | null;
	onClose: () => void;
	rfxId: number;
	setSubmissionStatus: (status: RfxSubmissionStatus) => void;
	companyId?: number | string;
	isGuest?: boolean;
	guestEmail?: string;
}

export const AcceptDeclineModal: React.FC<AcceptDeclineModalProps> = ({
	showDialog,
	onClose,
	rfxId,
	setSubmissionStatus,
	companyId,
	isGuest = false,
}) => {
	const hasAccepted = showDialog === "accept";
	const showCompanyInfo = isGuest && hasAccepted;

	const acceptDialogBase = {
		icon: "check-circle-blue",
		bgColor: "bg-brand-secondary",
		iconColor: "text-utility-brand-700",
		title: "Accept Invitation",
		description: (
			<>
				By accepting this invitation you can start your submission.
				<br />
				<br />
				We will also email the Client to let them know you have accepted their
				invitation.
			</>
		),
	};

	const createDialogForGuest = () => {
		const dialog = acceptDialogBase;
		dialog.title = "Accept Invitation & Create Company Account";
		dialog.description = (
			<InfoBox>
				<p>
					Create your company account to start your submission.
					<br />
					Build your profile, showcase your projects, add key team members, and
					submit your response with ease.
				</p>
				<br />
				We’ll let the Client know once you’ve accepted.
			</InfoBox>
		);
		return dialog;
	};

	const dialogProperty = showCompanyInfo
		? createDialogForGuest()
		: hasAccepted
			? acceptDialogBase
			: {
					icon: "request",
					bgColor: "bg-utility-error-50",
					iconColor: "text-utility-error-500",
					title: "Decline Request",
					description: (
						<>
							Are you sure you want to decline this request?
							<br />
							<br />
							This action cannot be undone.
						</>
					),
				};

	const [reason, setReason] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const onClickAcceptDeclineHandler = async () => {
		return onClickAcceptDeclineHandlerGuestAndSupplier(null);
	};

	const onClickAcceptDeclineHandlerGuestAndSupplier = async (
		companyData: GuestCompanyData
	) => {
		if (!hasAccepted && isNull(reason)) return;

		setIsLoading(true);
		try {
			const response = await acceptDeclineSubmission(
				rfxId,
				hasAccepted,
				reason,
				companyData
			);

			if (response) {
				if (isGuest && hasAccepted) {
					const user = response.user;
					const company = response.company;
					setSwitchFromGuest(company, user, `/account/${company.id}/rfx`);
				}

				if (hasAccepted) {
					setSubmissionStatus(RFX_STATUS.ACCEPTED);
				} else {
					setSubmissionStatus(RFX_STATUS.DECLINE);
					setTimeout(() => {
						navigate(isGuest ? "/account/rfx" : `/account/${companyId}/rfx`);
					}, 100);
				}
				onClose();
			}
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const FooterComponent = () => (
		<div className="flex w-full justify-between gap-2 text-utility-error-50">
			<Button
				onClick={onClose}
				btntype="base"
				className="!w-full"
				disabled={isLoading}
			>
				{hasAccepted ? "Cancel" : "Cancel"}
			</Button>
			<Button
				className="flex !w-full gap-3"
				onClick={onClickAcceptDeclineHandler}
				disabled={isLoading}
				data-testid="submit-button-modal"
			>
				{isLoading ? <Spinner /> : hasAccepted ? "Accept" : "Decline"}
			</Button>
		</div>
	);

	return (
		<CustomModal
			onClose={onClose}
			isOpen={!!showDialog}
			header={dialogProperty}
			size={showCompanyInfo ? "6xl" : "md"}
			footerComponent={!showCompanyInfo && <FooterComponent />}
		>
			{showCompanyInfo && (
				<CompanyInfo
					onSubmit={onClickAcceptDeclineHandlerGuestAndSupplier}
					onCancel={onClose}
					isLoading={isLoading}
				/>
			)}
			<div>
				{!hasAccepted && (
					// @ts-ignore
					<Input
						label="Reason"
						name="reason"
						type="textArea"
						placeholder="Enter reason for decline"
						required={true}
						error={isNull(reason) ? "Reason is required" : undefined}
						onChange={(
							e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
						) => setReason(e.target.value)}
					/>
				)}
			</div>
		</CustomModal>
	);
};
